import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import clsx from 'clsx';
import {
	Box,
	Button,
	CircularProgress,
	Grid,
	Typography,
	makeStyles,
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { PdfViewer, Topbar, Footer } from 'components';
import LoaderCustom from 'components/LoaderCustom';
import { 
	getAllAplicationsForApplicantPost,
	getJobDetailsPost, 
	viewSubmittedJobApplicationPdfPost,
} from 'actions';
import useRouter from 'utils/useRouter';
import { EncryptPayloadData } from 'EncryptionDecryption/EncryptAndDecrypt';
import { getApplicantGuid } from 'utils/settings';
import secureStorage from 'utils/secureStorage';
import ModalCustom from 'components/ModalCustom';
import { Header } from 'views/Wizard/components';
import { DesktopApplicationTable, MobileApplicationList } from './components';

const useStyles = makeStyles(theme => ({
	applyNowButton: {
		width: '60%',
	},
	applyNowOutlined: {
		width: '100%',
	},
	inProgressBox: {
		border: '1px solid #e0e0e0',
		borderRadius: theme.spacing(0.5),
		padding: theme.spacing(2),
		width: '100%',
		marginTop: theme.spacing(2),
		backgroundColor: theme.palette.white,
		textAlign: 'center',
	},
	lastSaved: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	resumeButton: {
		width: '100%',
	},
	orDivider: {
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(2),
	},
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		backgroundColor: theme.palette.white,
		height: '100%',
		overflow: 'auto',
		paddingBottom: theme.spacing(10),
		paddingTop: theme.spacing(10),
		[theme.breakpoints.down('sm')]: {
			height: '91%',
			paddingTop: theme.spacing(8),
		},
		'&.hasLogo': {
			paddingTop: theme.spacing(12),
			[theme.breakpoints.down('xs')]: {
				paddingTop: theme.spacing(15),
			},
		},
	},
	jobDescription: {
		marginBottom: theme.spacing(2),
	},
	jobSection: {
		backgroundColor: '#f4f6f8',
		borderRadius: theme.spacing(0.5),
		marginTop: theme.spacing(3),
		padding: theme.spacing(2),
		width: '40%',
		[theme.breakpoints.down('sm')]: {
			width: 'unset',
		},
	},
	label: {
		alignSelf: 'flex-start',
		marginBottom: theme.spacing(2),
		marginLeft: '5%',
		marginTop: theme.spacing(8),
		[theme.breakpoints.down('sm')]: {
			marginLeft: theme.spacing(2),
		},
	},
	loader: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
		width: '100%',
		[theme.breakpoints.down('xs')]: {
			zIndex: '1',
		},
	},
	modalButtons: {
		display: 'flex',
		justifyContent: 'space-between',
		marginTop: theme.spacing(3),
		[theme.breakpoints.down('xs')]: {
			flexDirection: 'column',
		},
	},
	modalButton: {
		color: theme.palette.text.light,
		[theme.breakpoints.down('xs')]: {
			marginBottom: theme.spacing(2),
		},
	},
	overflowText: {
		overflowWrap: 'anywhere',
	},
	paper: {
		width: '90%', 
		boxShadow: 'none',
		[theme.breakpoints.down('sm')]: {
			width: '100%',
		},
	},
	spinner: {
		textAlign: 'center',
	},
	tableBorder: {
		border: '1px solid #ddd',
	},
	tableContainer: {
		maxHeight: theme.spacing(50),
		position: 'sticky',
	},
	boldText: {
		fontWeight: theme.typography.fontWeightBold,
	},
}));

const ApplicantLandingPage = () => {
	const classes = useStyles();
	const router = useRouter();
	const dispatch = useDispatch();
	const { 
		jobApplications, 
		isJobApplicationsLoading, 
		jobDetails, 
		isLoggedIn, 
		isLoading,  
		viewSubmittedJobApplicationPdf,
		isViewSubmittedJobApplicationPdfLoading,
		jobUrlApplication,
		...auth
	} = useSelector(state => state.auth);
	const [ openPDFDialog, setOpenPDFDialog ] = useState(false);
	const [ pdfFilePath, setPdfFilePath ] = useState('');
	const [ isViewButtonClicked, setIsViewButtonClicked ] = useState(false);

	const [ isConfirmationModal, setIsConfirmationModal ] = useState(false);
	const [ buttonType, setButtonType ] = useState(''); // 'applyNow', 'continueApplication', 'editApplication', 
	const [ selectedApplication, setSelectedApplication ] = useState({});
	const [ isApiCall, setIsApiCall ] = useState(false);

	const applyNowMessage = `Once you start a new application, you can leave at anytime and your changes will be saved as pending with an incomplete status. 
		You will need to complete the full application process to submit the application for the job position.`
	
	const editApplicationMessage = 'We see that you have submitted an application for this position. If you would like to edit this application, you will need to complete the full application process and submit the application again.';
	const continueApplicationMessage = 'We see that you have a pending application for this position. If you would like to resume this application, you will need to complete the full application process and submit the application.';
	
	if (!isLoggedIn) {
		const param = secureStorage.getItem('jobUrlParam');
		const jobPath = (param?.ClientSuffix && param?.JobCode)
			? `/application/${param.ClientSuffix}/${param.JobCode}`
			: '/application';

		return  <Redirect to={jobPath} />
	}

	useEffect(() => {
		const details = {
            applicantGuid: getApplicantGuid(),
        }
		var payloadVal = EncryptPayloadData("getAllAplicationsForApplicant", details, auth);
		dispatch(getAllAplicationsForApplicantPost(payloadVal[0], payloadVal[1]));
	}, []);

	useEffect(() => {
		if (router.history.action === 'POP' && jobDetails?.jobUrlLink) {
			const cleanUrl = jobDetails.jobUrlLink.split('?')[0];
			const urlParts = cleanUrl.split('/').filter(part => part !== '');
			const clientSuffix = urlParts[urlParts.length - 2];
			const jobCode = urlParts[urlParts.length - 1];
			if (clientSuffix && jobCode) {
				const params = { clientSuffix, jobCode };
				const payloadVal = EncryptPayloadData("getJobOpeningDetails", params, auth);
				dispatch(getJobDetailsPost(payloadVal[0], payloadVal[1]));
			}
		}
	}, []);

	const handleOpenConfirmationModal = () => {
		setIsConfirmationModal(true);
	};

	const handleCloseConfirmationModal = () => {
		setIsConfirmationModal(false);
	};

	const getJobDetails = () => {
		const params = {
            clientSuffix: selectedApplication?.clientSuffix,
			jobCode: selectedApplication?.jobCode,
        }
		var payloadVal = EncryptPayloadData("getJobOpeningDetails", params, auth);
		dispatch(getJobDetailsPost(payloadVal[0], payloadVal[1]));
		setIsApiCall(true);
		secureStorage.setItem('currentJobParam', {
			clientSuffix: selectedApplication?.clientSuffix,
			jobCode: selectedApplication?.jobCode,
		});
	};

	const handleConfirmation = () => {
		switch (buttonType) {
			case 'applyNow':
				router.history.push('/wizard');
				break;
			case 'editApplication': {
				const params = secureStorage.getItem('jobUrlParam');

				if ( params?.clientSuffix !== selectedApplication?.clientSuffix && params?.jobCode !== selectedApplication?.jobCode) {
					getJobDetails();
				}
			}
				break;
			case 'continueApplication': {
				const params = secureStorage.getItem('jobUrlParam');

				if ( params?.clientSuffix !== selectedApplication?.clientSuffix && params?.jobCode !== selectedApplication?.jobCode) {
					getJobDetails();
				}

				break;
			}
			default: break;
		}

		handleSessionStorage();
		handleCloseConfirmationModal();
	};

	const handleSessionStorage = () =>{
		secureStorage.setItem('jobApplicationGuid', selectedApplication?.jobApplicationGuid);
		secureStorage.setItem('applicationParentGuid', selectedApplication?.applicationParentGuid);
		secureStorage.setItem('currentApplicationType', buttonType);
	};

	const handleApplyNow = () => {
		handleOpenConfirmationModal();
		setSelectedApplication({
			jobApplicationGuid: '', 
			applicationParentGuid: '',
		});
		setButtonType('applyNow');
	};

	const handleEditApplication = application => {
		const url = application?.url?.split('/')?.filter(code => code !== '');

		handleOpenConfirmationModal();
		setSelectedApplication({
			jobApplicationGuid: '', 
			applicationParentGuid: application?.applicationParentGuid,
			clientSuffix: url[0],
			jobCode: url[1],
		});
		setButtonType('editApplication');
	};

	const handleContinueApplication = application => {
		const url = application?.url?.split('/')?.filter(code => code !== '');

		handleOpenConfirmationModal();
		setSelectedApplication({
			jobApplicationGuid: application?.continueApplicationGuid, 
			applicationParentGuid: application?.applicationParentGuid,
			clientSuffix: url[0],
			jobCode: url[1],
		});
		setButtonType('continueApplication');
	};

	const handleViewApplication = application => {
		const data = { 
			"jobApplicationGuid": application?.viewApplicationGuid, 
			"countryId": 0
		}
		
		var payloadVal = EncryptPayloadData("getJobApplicationPdf", data, auth);

		dispatch(viewSubmittedJobApplicationPdfPost(payloadVal[0], payloadVal[1]));

		setSelectedApplication({
			jobApplicationGuid: application?.viewApplicationGuid, 
			applicationParentGuid: application?.applicationParentGuid,		
		});
		setIsViewButtonClicked(true);
	};

	const handleClosePDFDialog = () => {
		setOpenPDFDialog(false);
		setIsViewButtonClicked(false);
	}

	useEffect(() => {
		if (isApiCall && !isLoading && Object.keys(jobDetails)?.length > 0) {
			router.history.push('/wizard');
		}
	}, [
		isApiCall,
		isLoading,
		jobDetails,
	]);

	useEffect(() => {
		if (
			isViewButtonClicked 
			&& !isViewSubmittedJobApplicationPdfLoading 
			&& Object.keys(viewSubmittedJobApplicationPdf)?.length > 0
		) {
			setOpenPDFDialog(true);
			setPdfFilePath('data:application/pdf;base64,' + viewSubmittedJobApplicationPdf?.jobApplicationPdf);
			setIsViewButtonClicked(false);
		}

		if(isViewButtonClicked && isViewSubmittedJobApplicationPdfLoading) {
			setPdfFilePath('');
		}
	}, [
		isViewButtonClicked,
		isViewSubmittedJobApplicationPdfLoading,
		viewSubmittedJobApplicationPdf,
	]);
    
	return (
		<>
			<Topbar />
			<main className={clsx(classes.content, classes.container, jobDetails?.logoInBase64Format && 'hasLogo')}>
				{(isJobApplicationsLoading || isLoading) 
					? <Box className={classes.loader} component="div">
						<CircularProgress size={80} className={classes.spinner} />
					</Box>
					: <>
						<Header firstName={jobUrlApplication?.firstName} lastName={jobUrlApplication?.lastName} middleName={jobUrlApplication?.middleName} />
						<div className={clsx(classes.jobSection, classes.container)}>
							<Typography variant="h5" gutterBottom align="center">
								{jobDetails.jobTitle}
							</Typography>
							<Typography  
								variant="body1" 
								align="center"
								className={clsx(classes.overflowText, classes.jobDescription)}
							>
								{jobDetails.jobDescription}
							</Typography>
							{(jobUrlApplication?.continueApplicationGuid || jobUrlApplication?.editApplicationGuid) && (
								<>
									<Box className={classes.inProgressBox}>
										<Typography variant="body1">
											{jobUrlApplication?.continueApplicationGuid ? 'You have an application in progress.' : 'You have a previously submitted application.'}
										</Typography>
										<Typography variant="body1" className={classes.lastSaved}>
											{ jobUrlApplication?.continueApplicationGuid 
												? `Last saved: ${new Date(jobUrlApplication?.lastEditedDate)?.toLocaleDateString('en-US')}`
												: `Date Submitted: ${new Date(jobUrlApplication?.submittedDate)?.toLocaleDateString('en-US')}`
											}
										</Typography>
										<Button
											className={classes.resumeButton}
											variant="contained"
											color="secondary"
											onClick={() => jobUrlApplication?.continueApplicationGuid ? handleContinueApplication(jobUrlApplication) : handleEditApplication(jobUrlApplication)}
										>
											{jobUrlApplication?.continueApplicationGuid ? 'Resume Application' : 'Edit Application'}
										</Button>
									</Box>
									<Typography variant="body2" className={classes.orDivider}>
										or
									</Typography>
								</>
							)}
							<Box className={classes.inProgressBox}>
							<Typography variant="body1" gutterBottom align="center" className={classes.boldText}>
									Start new application
								</Typography>
								<Button
									className={classes.applyNowOutlined}
									variant="outlined"
									color="secondary"
									onClick={handleApplyNow}
								>
									Apply Now
								</Button>
							</Box>
						</div>
						{jobApplications?.length > 0 && <Typography className={classes.label} variant="h5">
							YOUR APPLICATIONS
						</Typography>}
						{jobApplications?.length > 0 && (
							<>
							{/* We have separate UIs for Mobile and Desktop. 
								If you need to add any new element or data field, 
								please make sure to add it in both UIs. 
							*/}
								<DesktopApplicationTable
									applications={jobApplications}
									onView={handleViewApplication}
									onEdit={handleEditApplication}
									onContinue={handleContinueApplication}
								/>
								<MobileApplicationList
									applications={jobApplications}
									onView={handleViewApplication}
									onEdit={handleEditApplication}
									onContinue={handleContinueApplication}
								/>
							</>
						)}
					</>}                                                                             
			</main>
			<Footer />
			<ModalCustom
				title={buttonType === 'applyNow' 
					? 'Apply Now' 
					: buttonType === 'editApplication' ? 'Edit Application' : 'Resume Application'}
				open={isConfirmationModal}
				close={handleCloseConfirmationModal}
				actions={false}
				backDrop={true}
				width="md"
			>
				<Typography color="inherit" variant="subtitle1">
					{buttonType === 'applyNow' 
						? applyNowMessage 
						: buttonType === 'editApplication' ? editApplicationMessage : continueApplicationMessage}
				</Typography>
				<Grid className={classes.modalButtons}>
					<Button
						color="inherit"
						variant="outlined"
						onClick={handleCloseConfirmationModal}
						className={classes.modalButton}
					>
						Cancel
					</Button>
					<Button
						color="secondary"
						variant="contained"
						onClick={handleConfirmation}
					>
						Continue
					</Button>
				</Grid> 
			</ModalCustom>
			<PdfViewer
				isOpenPdfViewer={openPDFDialog}
				onClose={handleClosePDFDialog}
				pdfFile={pdfFilePath}
				title="Submitted Application"
				downloadFileName={viewSubmittedJobApplicationPdf?.fileName}
			/>
			<LoaderCustom isLoading={isViewSubmittedJobApplicationPdfLoading} />
		</>
	);
};

export default ApplicantLandingPage;